import { useEffect, useState, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, Boxes } from "lucide-react";
import { toast } from "sonner";
import { STANDARD_UNITS } from "@/lib/supplierHelpers";

// formatCurrency is kept for eventual valorization display ; not used here yet
void formatCurrency;

const TYPE_LABEL = {
  adjustment: "Ajustement",
  purchase: "Achat",
  correction: "Correction",
};
const TYPE_COLOR = {
  adjustment: "bg-slate-100 text-slate-800",
  purchase: "bg-green-100 text-green-800",
  correction: "bg-amber-100 text-amber-800",
};

export const ProductStockDialog = ({ open, onClose, product }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ entries: [], current_stock: 0, reference_unit: "" });
  const [form, setForm] = useState({
    quantity: "", unit: product?.reference_unit || "kg",
    entry_date: new Date().toISOString().slice(0, 10), type: "adjustment", note: "",
  });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    if (!product) return;
    try {
      setLoading(true);
      const { data } = await api.getProductStock(product.id);
      setData(data);
      setForm((f) => ({ ...f, unit: data.reference_unit }));
    } catch (_e) {
      toast.error("Erreur chargement stock");
    } finally {
      setLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (open) fetch();
  }, [open, fetch]);

  const handleAdd = async () => {
    if (!form.quantity || !form.unit || !form.entry_date) {
      toast.error("Quantité, unité et date requis");
      return;
    }
    try {
      setSaving(true);
      await api.addStockEntry(product.id, {
        quantity: parseFloat(form.quantity),
        unit: form.unit,
        entry_date: form.entry_date,
        type: form.type,
        note: form.note || null,
      });
      toast.success("Entrée stock ajoutée");
      setForm({
        quantity: "", unit: data.reference_unit, entry_date: new Date().toISOString().slice(0, 10),
        type: "adjustment", note: "",
      });
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette entrée stock ?")) return;
    try {
      await api.deleteStockEntry(id);
      toast.success("Entrée supprimée");
      fetch();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-primary" />
            Stock — {product?.name}
          </DialogTitle>
          <DialogDescription>
            Saisissez manuellement les entrées de stock (achat, ajustement, correction) avec leur date.
            Le stock actuel est la somme de toutes les entrées converties en unité de référence.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Stock actuel</div>
                    <div className="text-3xl font-bold text-primary font-mono" data-testid="current-stock">
                      {data.current_stock ?? 0} <span className="text-base">{data.reference_unit}</span>
                    </div>
                    {product?.min_stock_threshold != null && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Seuil minimum : <span className="font-semibold text-amber-700">{product.min_stock_threshold} {data.reference_unit}</span>
                      </div>
                    )}
                  </div>
                  <Badge variant="outline">{data.entries?.length || 0} entrée(s)</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <h3 className="font-medium text-sm mb-2">Nouvelle entrée</h3>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Quantité *</Label>
                    <Input
                      type="number" step="0.01" value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      data-testid="stock-qty"
                    />
                    <p className="text-xs text-muted-foreground mt-0.5">Négatif = sortie</p>
                  </div>
                  <div>
                    <Label className="text-xs">Unité *</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger data-testid="stock-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STANDARD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date" value={form.entry_date}
                      onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                      data-testid="stock-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="stock-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adjustment">Ajustement</SelectItem>
                        <SelectItem value="purchase">Achat</SelectItem>
                        <SelectItem value="correction">Correction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Note</Label>
                    <Input
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Ex : inventaire semestriel..."
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={handleAdd} disabled={saving} data-testid="add-stock-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                    Ajouter
                  </Button>
                </div>
              </CardContent>
            </Card>

            {data.entries?.length > 0 && (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Quantité</TableHead>
                        <TableHead className="text-right">Équivalent {data.reference_unit}</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.entries.map((e) => (
                        <TableRow key={e.id} data-testid={`stock-entry-${e.id}`}>
                          <TableCell className="font-mono text-sm">{new Date(e.entry_date).toLocaleDateString("fr-FR")}</TableCell>
                          <TableCell><Badge className={TYPE_COLOR[e.type]}>{TYPE_LABEL[e.type]}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{e.quantity} {e.unit}</TableCell>
                          <TableCell className={`text-right font-mono font-semibold ${e.reference_qty < 0 ? "text-red-600" : "text-green-700"}`}>
                            {e.reference_qty != null ? `${e.reference_qty > 0 ? "+" : ""}${e.reference_qty} ${data.reference_unit}` : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{e.note || ""}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(e.id)} data-testid={`delete-stock-${e.id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductStockDialog;
